import { useCallback, useState, useEffect, useRef } from "react";
import { generateFileThumbnail } from "@utils/videoEditing";
import { useMediaStore } from "@/store";
import { MediaItem, MediaItemStatus } from "@/types/media";
import { useKeyboardShortcut } from "./useKeyboardShortcut";
import { useMediaUtils } from "./useMediaUtils";

/**
 * Shared hook for file carousel navigation
 * Handles prev/next file navigation with keyboard support
 */
export function useFileCarouselNavigation({
    mediaItems,
    fileId,
    flowLocked,
    hasMultipleFiles
}: {
    mediaItems: MediaItem[];
    fileId?: string;
    flowLocked: boolean;
    hasMultipleFiles: boolean;
}) {
    const { setCurrentMedia } = useMediaUtils();
    const media = useMediaStore();
    const currentIndex = mediaItems.findIndex((f) => f.id === fileId);

    const handlePrevFile = useCallback(() => {
        if (flowLocked || !hasMultipleFiles) return;

        if (mediaItems.length > 0) {
            const prevIndex = currentIndex <= 0 ? mediaItems.length - 1 : currentIndex - 1;

            const targetMediaItem = mediaItems[prevIndex];
            let targetFrame = targetMediaItem;
            if (targetMediaItem.isVideo) {
                const frameId = targetMediaItem.segmentToFrameMediaItemIdMap?.[0];
                targetFrame = media[frameId!];
            }

            setCurrentMedia({ file: targetMediaItem, frame: targetFrame, frameSegment: 0 });
        }
    }, [mediaItems, currentIndex, flowLocked, hasMultipleFiles, setCurrentMedia, media]);

    const handleNextFile = useCallback(() => {
        if (flowLocked || !hasMultipleFiles) return;

        if (mediaItems.length > 0) {
            const nextIndex = currentIndex >= mediaItems.length - 1 ? 0 : currentIndex + 1;

            const targetMediaItem = mediaItems[nextIndex];
            let targetFrame = targetMediaItem;
            if (targetMediaItem.isVideo) {
                const frameId = targetMediaItem.segmentToFrameMediaItemIdMap?.[0];
                targetFrame = media[frameId!];
            }

            setCurrentMedia({ file: targetMediaItem, frame: targetFrame, frameSegment: 0 });
        }
    }, [mediaItems, currentIndex, flowLocked, hasMultipleFiles, setCurrentMedia, media]);

    return { currentIndex, handlePrevFile, handleNextFile };
}

export function useFileCarouselThumbnails(
    mediaItems: MediaItem[],
    orderedTimelineFramePreviewUrls: any,
    highResolution: boolean = false
) {
    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
    const generatedAtSize = useRef<Record<string, number>>({});

    useEffect(() => {
        let cancelled = false;
        const thumbnailSize = highResolution ? 1024 : 64;

        const itemsToGenerate = mediaItems.filter((item) => {
            if (item.status === MediaItemStatus.PROCESSING) return false;
            return generatedAtSize.current[item.id] !== thumbnailSize;
        });

        if (itemsToGenerate.length === 0) return;

        Promise.allSettled(
            itemsToGenerate.map(async (mediaItem) => {
                let thumbnail: string | null = null;

                if (mediaItem.isVideo) {
                    const cached =
                        orderedTimelineFramePreviewUrls[mediaItem.id]?.[
                            mediaItem.metadata?.frame_count!
                        ];
                    thumbnail = cached
                        ? cached[0]
                        : await generateFileThumbnail(mediaItem.file, { size: thumbnailSize });
                } else {
                    thumbnail = await generateFileThumbnail(mediaItem.file, {
                        size: thumbnailSize
                    });
                }

                if (thumbnail && !cancelled) {
                    generatedAtSize.current[mediaItem.id] = thumbnailSize;
                    setThumbnails((prev) => ({ ...prev, [mediaItem.id]: thumbnail! }));
                }
            })
        );

        return () => {
            cancelled = true;
        };
    }, [mediaItems, orderedTimelineFramePreviewUrls, highResolution]);

    return thumbnails;
}

/**
 * Shared hook for keyboard navigation in file carousel
 * Handles up/down and left/right arrow keys for file navigation
 */
export function useFileCarouselKeyboardNavigation(
    hasMultipleFiles: boolean,
    flowLocked: boolean,
    handlePrevFile: () => void,
    handleNextFile: () => void
) {
    const disabled = !hasMultipleFiles || flowLocked;

    // Vertical navigation (up/down)
    useKeyboardShortcut({ key: "ArrowUp", handler: handlePrevFile, disabled });
    useKeyboardShortcut({ key: "ArrowDown", handler: handleNextFile, disabled });

    // Horizontal navigation (left/right) - useful for comparison mode and general UX
    useKeyboardShortcut({ key: "ArrowLeft", handler: handlePrevFile, disabled });
    useKeyboardShortcut({ key: "ArrowRight", handler: handleNextFile, disabled });
}

/**
 * Shared hook for auto-scrolling to current file in carousel
 * Automatically scrolls the thumbnail container to keep the current file centered
 */
export function useFileCarouselAutoScroll({
    fileId,
    hasMultipleFiles,
    containerId
}: {
    fileId?: string;
    hasMultipleFiles: boolean;
    containerId: string;
}) {
    useEffect(() => {
        if (fileId && hasMultipleFiles) {
            const thumbnailElement = document.querySelector(`[data-file-id="${fileId}"]`);
            if (thumbnailElement) {
                const container = document.getElementById(containerId);
                if (container) {
                    const elementRect = thumbnailElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    // Calculate the scroll position to center the element
                    const elementCenter = elementRect.top + elementRect.height / 2;
                    const containerCenter = containerRect.top + containerRect.height / 2;
                    const scrollOffset = elementCenter - containerCenter;
                    const scrollTop = container.scrollTop + scrollOffset;

                    container.scrollTo({ top: scrollTop, behavior: "smooth" });
                }
            }
        }
    }, [fileId, hasMultipleFiles, containerId]);
}
